/**
 * "Resend it" action for the unverified-email banner.
 *
 * ABUSE SHAPE THIS GUARDS AGAINST: every click sends real mail to a real
 * inbox. An impatient user clicking five times is indistinguishable from an
 * attacker using the button as a mail cannon, so the control is layered:
 *
 *   1. an in-flight latch, so a double-click cannot fire twice;
 *   2. a visible cooldown after each send, so the button cannot be spammed;
 *   3. the cooldown persists in localStorage, so a page refresh does not
 *      reset it;
 *   4. the server's own 3/hour limit (RATE_LIMITS.passwordReset), which is the
 *      real ceiling — everything above is UX so users meet it less often.
 *
 * The endpoint is Turnstile-gated like every other mail-sending auth route, so
 * this renders its own widget. That guard is deliberate: adding
 * /send-verification-email to UNGUARDED_AUTH_PATHS would REMOVE a security
 * control (see CLAUDE.md), so the widget is the correct answer, not an
 * exemption.
 */
import { useEffect, useRef, useState } from 'react';
import { authClient } from '../auth';
import { useConfig } from '../hooks/useConfig';
import { Turnstile } from './Turnstile';

/** Keeps users clear of the server's 3/hour budget without feeling punitive. */
const COOLDOWN_SECONDS = 60;
const COOLDOWN_KEY = 'ideabrief.verifyResendAt.v1';

type State = 'idle' | 'sending' | 'sent' | 'error';

function secondsRemaining(): number {
  try {
    const raw = localStorage.getItem(COOLDOWN_KEY);
    if (!raw) return 0;
    const elapsed = (Date.now() - Number(raw)) / 1000;
    return Math.max(0, Math.ceil(COOLDOWN_SECONDS - elapsed));
  } catch {
    return 0;
  }
}

export function ResendVerification({ email }: { email: string }) {
  const config = useConfig();
  const [state, setState] = useState<State>('idle');
  const [cooldown, setCooldown] = useState(secondsRemaining);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  /** Latch: guards against a second submit landing before state updates. */
  const inFlight = useRef(false);

  // Tick the visible countdown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => {
      const left = secondsRemaining();
      setCooldown(left);
      if (left <= 0) clearInterval(t);
    }, 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  async function resend() {
    if (inFlight.current || cooldown > 0) return;
    if (!captchaToken) {
      setMessage("We're still verifying you're human — give it a moment.");
      return;
    }

    inFlight.current = true;
    setState('sending');
    setMessage(null);

    try {
      const { error } = await authClient.sendVerificationEmail({
        email,
        callbackURL: '/ideas',
        fetchOptions: { body: { captchaToken } },
      });
      if (error) throw error;

      // Start the cooldown only on success, so a failed attempt does not lock
      // the user out of retrying.
      try {
        localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
      } catch {
        // A missing cooldown just means the server limit is the only guard.
      }
      setCooldown(COOLDOWN_SECONDS);
      setState('sent');
      setMessage(`Sent again to ${email}. Check your inbox and spam folder.`);
    } catch (err) {
      setState('error');
      const status =
        typeof err === 'object' && err !== null && 'status' in err
          ? Number((err as { status: unknown }).status)
          : 0;
      setMessage(
        status === 429
          ? "That's a few too many for now. Try again in an hour, or check your spam folder — the earlier emails may already be there."
          : "We couldn't send it just now. Try again in a moment.",
      );
      // The token is single-use; a retry needs a fresh one either way.
      setCaptchaToken('');
      setCaptchaNonce((n) => n + 1);
    } finally {
      inFlight.current = false;
    }
  }

  const busy = state === 'sending';
  const blocked = busy || cooldown > 0;

  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
      >
        <span style={{ fontSize: 13.5, color: 'var(--ink2)' }}>
          Didn&apos;t get it?
        </span>
        <button
          type="button"
          onClick={resend}
          disabled={blocked}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            fontSize: 13.5,
            fontWeight: 600,
            color: blocked ? 'var(--muted)' : 'var(--acc)',
            cursor: blocked ? 'not-allowed' : 'pointer',
            textDecoration: blocked ? 'none' : 'underline',
          }}
        >
          {busy
            ? 'Sending…'
            : cooldown > 0
              ? `Resend available in ${cooldown}s`
              : 'Resend the email'}
        </button>
      </div>

      {/* Rendered only while a send is actually possible, so the banner stays
          quiet for the common case of a user who is not resending. */}
      {config.turnstileSiteKey && cooldown <= 0 && state !== 'sent' && (
        <div style={{ marginTop: 10 }}>
          <Turnstile
            siteKey={config.turnstileSiteKey}
            onToken={setCaptchaToken}
            resetKey={captchaNonce}
          />
        </div>
      )}

      {message && (
        <p
          role="status"
          style={{
            margin: '8px 0 0',
            fontSize: 13,
            lineHeight: 1.55,
            color: state === 'error' ? 'oklch(0.55 0.19 25)' : 'var(--ink2)',
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
}
